import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from '@/hooks/use-toast';

interface ContentTabProps {
  title: string;
  description: string;
  content: string;
  lastUpdated: string;
  onSave: (content: string) => void;
  isSaving: boolean;
}

export function ContentTab({ title, description, content, lastUpdated, onSave, isSaving }: ContentTabProps) {
  const [editorContent, setEditorContent] = useState<string>(content);
  const { toast } = useToast();

  // Update editor content when the content prop changes
  useEffect(() => {
    setEditorContent(content);
  }, [content]);

  const handleSave = () => {
    if (!editorContent.trim()) {
      toast({
        title: "Content required",
        description: "Please enter some content.",
        variant: "destructive",
      });
      return;
    }

    onSave(editorContent);
  };

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setEditorContent(e.target.value);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>
          {description}
          {lastUpdated && (
            <p className="text-xs mt-1 text-muted-foreground">
              Last updated: {new Date(lastUpdated).toLocaleString()}
            </p>
          )}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          <div className="space-y-2">
            <Textarea
              value={editorContent}
              onChange={handleTextareaChange}
              placeholder="Enter your content here..."
              className="min-h-[400px] resize-none"
            />
          </div>
          <div className="flex justify-end">
            <Button 
              onClick={handleSave}
              disabled={isSaving}
            >
              {isSaving ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}